import {
  CONFIRMATION_STATUS_LABELS,
  DEVICE_STATUS_LABELS,
  READING_STATUS_LABELS,
  REGISTER_BINDING_STATUS_LABELS,
  REQUEST_STATUS_LABELS,
} from './kioskConstants';
import { parseWeightValue } from './kioskFormatters';

export function createEmptyRequestLoadForm(createdBy = '', direction = 'Outbound') {
  return {
    name: '',
    direction: direction === 'Inbound' ? 'Inbound' : 'Outbound',
    linkedOutboundLoadId: '',
    createdBy,
    notes: '',
  };
}

export function createRequestLoadSummaryState(overrides = {}) {
  return {
    requestCount: 0,
    totalWeight: 0,
    requests: [],
    status: 'idle',
    message: '',
    ...overrides,
  };
}

export function buildInboundRequestLoadPrefix(linkedOutboundRequestLoad) {
  if (!linkedOutboundRequestLoad?.name) {
    return '';
  }

  return `ขาเข้า - ${linkedOutboundRequestLoad.name}`;
}

export function buildDisplayedRequestLoadName(requestLoadForm, linkedOutboundRequestLoad) {
  const rawName = requestLoadForm.name;
  if (requestLoadForm.direction !== 'Inbound') {
    return rawName;
  }

  const prefix = buildInboundRequestLoadPrefix(linkedOutboundRequestLoad);
  if (!prefix) {
    return rawName;
  }

  return rawName ? `${prefix} - ${rawName}` : prefix;
}

export function buildRequestLoadName(requestLoadForm, linkedOutboundRequestLoad) {
  const trimmedName = requestLoadForm.name.trim();
  if (requestLoadForm.direction !== 'Inbound') {
    return trimmedName;
  }

  const prefix = buildInboundRequestLoadPrefix(linkedOutboundRequestLoad);
  if (!prefix) {
    return trimmedName;
  }

  return trimmedName ? `${prefix} - ${trimmedName}` : prefix;
}

export function sortBindings(bindings) {
  return [...bindings].sort((left, right) => left.registerId.localeCompare(right.registerId));
}

export function sortDevices(devices) {
  return [...devices].sort((left, right) => left.deviceId.localeCompare(right.deviceId));
}

export function sortRequestLoads(requestLoads) {
  return [...requestLoads].sort((left, right) => right.loadId.localeCompare(left.loadId));
}

export function sortRequests(requests) {
  return [...requests].sort((left, right) => {
    const timeDelta = new Date(right.issuedAtUtc) - new Date(left.issuedAtUtc);
    if (timeDelta !== 0) {
      return timeDelta;
    }

    return String(right.requestId ?? '').localeCompare(String(left.requestId ?? ''));
  });
}

export function sortReadings(readings) {
  return [...readings].sort((left, right) => {
    const timeDelta = new Date(right.occurredAtUtc) - new Date(left.occurredAtUtc);
    if (timeDelta !== 0) {
      return timeDelta;
    }

    return (right.id ?? 0) - (left.id ?? 0);
  });
}

export function mergeReadings(currentReadings, nextReading) {
  return sortReadings([
    nextReading,
    ...currentReadings.filter((reading) => reading.id !== nextReading.id),
  ]);
}

export function mergeRequests(currentRequests, nextRequest) {
  return sortRequests([
    nextRequest,
    ...currentRequests.filter((request) => request.requestId !== nextRequest.requestId),
  ]);
}

export function mergeRequestLoad(currentRequestLoads, nextRequestLoad) {
  const existingRequestLoad = currentRequestLoads.find((requestLoad) => (
    requestLoad.loadId === nextRequestLoad.loadId
  ));
  const mergedRequestLoad = existingRequestLoad
    ? {
        ...existingRequestLoad,
        ...nextRequestLoad,
        aggregate: nextRequestLoad.aggregate ?? existingRequestLoad.aggregate,
      }
    : nextRequestLoad;

  return sortRequestLoads([
    mergedRequestLoad,
    ...currentRequestLoads.filter((requestLoad) => requestLoad.loadId !== nextRequestLoad.loadId),
  ]);
}

export function buildDeviceMap(devices) {
  return new Map(devices.map((device) => [device.deviceId, device]));
}

export function getRegisterOperationBlocker(binding, deviceMap) {
  if (!binding) {
    return 'ยังไม่เลือกสถานีชั่งน้ำหนัก กรุณเลือกสถานีชั่งน้ำหนักก่อนเริ่มใช้งาน';
  }

  if (binding.status !== 'Active') {
    return `Register '${binding.registerId}' is not active.`;
  }

  const scanner = deviceMap.get(binding.scannerDeviceId);
  if (!scanner) {
    return `Scanner '${binding.scannerDeviceId}' is missing from device inventory.`;
  }

  if (scanner.deviceStatus !== 'Active') {
    return `Scanner '${scanner.deviceId}' must be Active before creating a load or request.`;
  }

  const scale = deviceMap.get(binding.scaleDeviceId);
  if (!scale) {
    return `Scale '${binding.scaleDeviceId}' is missing from device inventory.`;
  }

  if (scale.deviceStatus !== 'Active') {
    return `Scale '${scale.deviceId}' must be Active before creating a load or request.`;
  }

  return '';
}

export function normalizeEnumLabel(value, labels) {
  if (typeof value === 'number' && Number.isInteger(value) && value >= 0 && value < labels.length) {
    return labels[value];
  }

  return value;
}

export function normalizeDevice(device) {
  if (!device) {
    return device;
  }

  return {
    ...device,
    deviceStatus: normalizeEnumLabel(device.deviceStatus, DEVICE_STATUS_LABELS),
  };
}

export function normalizeBinding(binding) {
  if (!binding) {
    return binding;
  }

  return {
    ...binding,
    status: normalizeEnumLabel(binding.status, REGISTER_BINDING_STATUS_LABELS),
  };
}

export function normalizeRequestLoad(requestLoad, fallbackRegisterId = '') {
  if (!requestLoad) {
    return requestLoad;
  }

  return {
    ...requestLoad,
    registerId: requestLoad.registerId ?? fallbackRegisterId,
    status: normalizeEnumLabel(requestLoad.status, ['Open', 'InProgress', 'Completed', 'Cancelled']),
  };
}

export function normalizeRequest(request) {
  if (!request) {
    return request;
  }

  return {
    ...request,
    requestStatus: normalizeEnumLabel(request.requestStatus, REQUEST_STATUS_LABELS),
    confirmationStatus: normalizeEnumLabel(request.confirmationStatus, CONFIRMATION_STATUS_LABELS),
  };
}

export function normalizeReading(reading) {
  if (!reading) {
    return reading;
  }

  return {
    ...reading,
    readingStatus: normalizeEnumLabel(reading.readingStatus, READING_STATUS_LABELS),
  };
}

export function getReadingDeviceType(reading, deviceMap) {
  if (!reading) {
    return '';
  }

  const deviceType = deviceMap.get(reading.deviceId)?.deviceType ?? '';

  switch (deviceType.toLowerCase()) {
    case 'scanner':
      return 'เครื่องสแกน';
    case 'scale':
      return 'เครื่องชั่ง';
    default:
      return '';
  }
}

export function getReadingDeviceKind(reading, deviceMap) {
  if (!reading) {
    return '';
  }

  return deviceMap.get(reading.deviceId)?.deviceType ?? '';
}

export function isAcceptedReading(reading) {
  return reading?.readingStatus === 'Accepted';
}

export function isCompletedRequest(request) {
  return request?.requestStatus === 'Completed';
}

export function getRequestScaleWeight(request) {
  return parseWeightValue(
    request?.numericScaleValue
      ?? request?.scaleValue,
  );
}

export function getRequestPalletWeight(request) {
  return parseWeightValue(request?.palletWeight);
}

export function getRequestProductWeight(request) {
  const scaleWeight = Math.abs(getRequestScaleWeight(request));
  const palletWeight = Math.abs(getRequestPalletWeight(request));
  return Math.max(scaleWeight - palletWeight, 0);
}

export function getSignedRequestProductWeight(request) {
  const productWeight = getRequestProductWeight(request);
  return request?.requestDirection === 'Inbound' ? -productWeight : productWeight;
}

export function calculateLoadTotalWeight(requests) {
  return requests
    .filter(isCompletedRequest)
    .reduce((sum, request) => sum + getSignedRequestProductWeight(request), 0);
}

export function calculateSuggestedReturnQuantityPacks({
  availableQuantityPacks,
  loadProducts,
  scannedProduct,
}) {
  const availablePacks = Number(availableQuantityPacks);
  if (!Number.isInteger(availablePacks) || availablePacks <= 0) {
    return null;
  }

  return calculateMinimumReturnQuantityPacks({
    availableQuantityPacks: availablePacks,
    loadProducts,
    scannedProduct,
  }) ?? availablePacks;
}

export function calculateMinimumReturnQuantityPacks({
  availableQuantityPacks,
  loadProducts,
  scannedProduct,
}) {
  const availablePacks = Number(availableQuantityPacks);
  if (!Number.isInteger(availablePacks) || availablePacks <= 0) {
    return null;
  }

  const remainderPacks = calculateReturnRemainderQuantityPacks({
    loadProducts,
    scannedProduct,
  });
  return remainderPacks == null
    ? null
    : Math.min(remainderPacks, availablePacks);
}

export function calculateReturnRemainderQuantityPacks({
  loadProducts,
  scannedProduct,
}) {
  const matchingProducts = (Array.isArray(loadProducts) ? loadProducts : []).filter((product) => (
    matchesScannedProduct(product, scannedProduct)
  ));
  if (matchingProducts.length !== 1) {
    return null;
  }

  const matchingProduct = matchingProducts[0];
  const bookedPacks = Number(matchingProduct.bookedPacks);
  const scannedPacks = Number(matchingProduct.scannedPacks);
  const remainingPacks = Number.isInteger(bookedPacks) && Number.isInteger(scannedPacks)
    ? bookedPacks - scannedPacks
    : Number(matchingProduct.remainingPacks);
  return Number.isInteger(remainingPacks) && remainingPacks < 0
    ? Math.abs(remainingPacks)
    : null;
}

function matchesScannedProduct(loadProduct, scannedProduct) {
  if (!loadProduct || !scannedProduct) {
    return false;
  }

  const loadCanonicalId = normalizeIdentity(loadProduct.canonicalProductId);
  const scannedCanonicalId = normalizeIdentity(scannedProduct.canonicalProductId);
  let productMatches;
  if (loadCanonicalId && scannedCanonicalId) {
    productMatches = loadCanonicalId === scannedCanonicalId;
  } else {
    const loadProductName = normalizeIdentity(
      loadProduct.canonicalProductName ?? loadProduct.productName,
    );
    const scannedProductName = normalizeIdentity(
      scannedProduct.canonicalProductName ?? scannedProduct.name,
    );
    productMatches = Boolean(
      loadProductName
      && scannedProductName
      && loadProductName === scannedProductName,
    );
  }
  if (!productMatches) {
    return false;
  }

  const loadPalletTypeId = normalizeIdentity(loadProduct.palletTypeId);
  const scannedPalletTypeId = normalizeIdentity(scannedProduct.palletTypeId);
  if (loadPalletTypeId && scannedPalletTypeId) {
    return loadPalletTypeId === scannedPalletTypeId;
  }

  const loadPalletTypeName = normalizeIdentity(loadProduct.palletTypeName);
  const scannedPalletTypeName = normalizeIdentity(scannedProduct.palletTypeName);
  return !loadPalletTypeName
    || !scannedPalletTypeName
    || loadPalletTypeName === scannedPalletTypeName;
}

function normalizeIdentity(value) {
  return String(value ?? '').trim().toLowerCase();
}

export function deriveRequestLoadStatus(requestLoad, relatedRequests) {
  if (requestLoad?.status) {
    return requestLoad.status;
  }

  if (Array.isArray(relatedRequests)) {
    if (relatedRequests.length === 0) {
      return 'Open';
    }

    if (relatedRequests.some((request) => (
      request.requestStatus !== 'Completed' && request.requestStatus !== 'Cancelled'
    ))) {
      return 'InProgress';
    }

    return 'Completed';
  }

  if (requestLoad?.status) {
    return requestLoad.status;
  }

  return 'Open';
}

export function canCreateNextRequest(request) {
  if (!request) {
    return true;
  }

  return (
    (request.requestStatus === 'Completed' && request.confirmationStatus === 'Confirmed')
    || (request.requestStatus === 'Cancelled' && request.confirmationStatus === 'Rejected')
  );
}

export function getActiveRequestForRegister(requests, registerId) {
  if (!registerId) {
    return null;
  }

  return sortRequests(requests)
    .find((request) => request.registerId === registerId && canCreateNextRequest(request) === false) ?? null;
}
